import { db } from './db.js';
import { storage } from './storage.js';

/* Catálogo guardado en el dispositivo, por trozos. Antes se guardaba entero en una fila de IndexedDB y leerlo al
   arrancar deserializaba decenas de miles de títulos de golpe: una tarea de medio segundo que dejaba la pantalla
   de espectadores sin responder. Troceado (4 000 títulos por fila) cuesta algo más en total pero se lee con respiros
   entre trozos, sin bloquear. El formato antiguo (una fila con todo) se sigue leyendo y se reescribe troceado. */

const STORE = 'catalog';
const CHUNK = 4000;
const SEP = '\u0001';
const KINDS = ['live', 'vod', 'series'];

const breathe = () => new Promise((resolve) => setTimeout(resolve, 0));

const STAMPS = 'catalogAt'; // { idProveedor: momento de la última actualización del catálogo }
const COUNTS = 'catalogCounts'; // { idProveedor: [canales, películas, series] }

export const catalogStore = {
    /** Cuándo se actualizó por última vez el catálogo de un proveedor (0 si no se sabe). Sin leer el catálogo. */
    updatedAt(pid) {
        const all = storage.get(STAMPS, {}) || {};
        return Number(all[pid]) || 0;
    },

    /** Cuántos canales, películas y series tiene el catálogo guardado de un proveedor, sin leerlo: {live, vod, series} o null si no se sabe. */
    counts(pid) {
        const all = storage.get(COUNTS, {}) || {};
        const c = all[pid];
        return Array.isArray(c) && c.length === 3 ? { live: c[0], vod: c[1], series: c[2] } : null;
    },

    setCounts(pid, live, vod, series) {
        if (!pid) return;
        const all = storage.get(COUNTS, {}) || {};
        const c = all[pid];
        if (c && c[0] === live && c[1] === vod && c[2] === series) return;
        all[pid] = [live, vod, series];
        storage.set(COUNTS, all);
    },

    /** Lo mismo para catálogos guardados antes de que se anotaran: con la cabecera y el último trozo de cada lista (los demás van llenos). */
    async ensureCounts(pid) {
        const known = this.counts(pid);
        if (known) return known;
        try {
            const head = await db.get(STORE, pid);
            if (!head) return null;
            let live = 0;
            let vod = 0;
            let series = 0;
            if (head.__chunked) {
                const n = {};
                for (const kind of KINDS) {
                    const keys = (head.chunks && head.chunks[kind]) || [];
                    let count = 0;
                    if (keys.length) {
                        const last = await db.get(STORE, keys[keys.length - 1]);
                        count = (keys.length - 1) * CHUNK + (Array.isArray(last) ? last.length : 0);
                    }
                    n[kind] = count;
                }
                live = n.live;
                vod = n.vod;
                series = n.series;
            } else {
                live = (head.live || []).length;
                vod = (head.vod || []).length;
                series = (head.series || []).length;
            }
            this.setCounts(pid, live, vod, series);
            return { live, vod, series };
        } catch (e) {
            return null;
        }
    },

    setStamp(pid, ts) {
        if (!pid || !ts) return;
        const all = storage.get(STAMPS, {}) || {};
        if (all[pid] === ts) return;
        all[pid] = ts;
        storage.set(STAMPS, all);
    },

    /** Lo mismo para catálogos guardados antes de que existieran los sellos: se lee solo la cabecera. */
    async stamp(pid) {
        const known = this.updatedAt(pid);
        if (known) return known;
        try {
            const head = await db.get(STORE, pid);
            const ts = head ? (head.__chunked ? head.meta && head.meta.updatedAt : head.updatedAt) : 0;
            if (ts) this.setStamp(pid, ts);
            return ts || 0;
        } catch (e) {
            return 0;
        }
    },

    /** → el catálogo (con `live`, `vod`, `series`) o null si no hay nada guardado o está incompleto. */
    async load(pid) {
        if (!pid) return null;
        const head = await db.get(STORE, pid);
        if (!head) return null;
        if (!head.__chunked) {
            // Formato antiguo: todo en una fila. Se marca para que quien lo cargue lo vuelva a guardar troceado.
            try { Object.defineProperty(head, '__legacy', { value: true, enumerable: false }); } catch (e) { /* objeto congelado */ }
            return head;
        }
        const data = Object.assign({}, head.meta);
        for (const kind of KINDS) {
            const list = [];
            for (const key of head.chunks[kind] || []) {
                const part = await db.get(STORE, key);
                if (!Array.isArray(part)) return null; // le falta un trozo (escritura a medias): como si no hubiera caché
                for (let i = 0; i < part.length; i++) list.push(part[i]);
                await breathe();
            }
            data[kind] = list;
        }
        return data;
    },

    async save(pid, data) {
        if (!pid || !data) return;
        const prev = await db.get(STORE, pid).catch(() => null);
        const gen = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
        const chunks = {};
        for (const kind of KINDS) {
            const arr = Array.isArray(data[kind]) ? data[kind] : [];
            chunks[kind] = [];
            for (let i = 0, n = 0; i < arr.length || (i === 0 && n === 0); i += CHUNK, n++) {
                const key = [pid, gen, kind, n].join(SEP);
                await db.put(STORE, key, arr.slice(i, i + CHUNK));
                chunks[kind].push(key);
                await breathe();
            }
        }
        const meta = {};
        Object.keys(data).forEach((k) => { if (KINDS.indexOf(k) === -1) meta[k] = data[k]; });
        // La cabecera se escribe la última: hasta entonces sigue valiendo la versión anterior.
        await db.put(STORE, pid, { __chunked: 1, gen, meta, chunks });
        this.setStamp(pid, data.updatedAt || Date.now());
        this.setCounts(pid, (data.live || []).length, (data.vod || []).length, (data.series || []).length);
        if (prev && prev.__chunked) await this._deleteChunks(prev);
    },

    async remove(pid) {
        const head = await db.get(STORE, pid).catch(() => null);
        if (head && head.__chunked) await this._deleteChunks(head);
        await db.delete(STORE, pid);
        const counts = storage.get(COUNTS, {}) || {};
        if (counts[pid]) { delete counts[pid]; storage.set(COUNTS, counts); }
    },

    async _deleteChunks(head) {
        for (const kind of KINDS) {
            for (const key of (head.chunks && head.chunks[kind]) || []) await db.delete(STORE, key).catch(() => {});
        }
    }
};
